import { API_ENDPOINTS } from "lib/apiUrl";
import { buildQueryString } from "lib/buildQueryString";

export function stripHtml(html) {
  if (html == null) return "";
  const s = String(html);
  if (typeof document !== "undefined") {
    const div = document.createElement("div");
    div.innerHTML = s;
    const text = div.textContent || div.innerText || "";
    return text.replace(/\s+/g, " ").trim();
  }
  return s
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncatePlainText(text, maxLen = 50) {
  const t = String(text || "").trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(0, maxLen - 1)).trim()}…`;
}

export function getProjectCoverImage(project) {
  const urls = project?.imageUrl;
  if (!Array.isArray(urls) || urls.length === 0) return "";
  const first = urls[0];
  return typeof first === "string" ? first.trim() : "";
}

/** All gallery image URLs from `project.imageUrl`. */
export function getProjectGalleryImages(project) {
  const urls = project?.imageUrl;
  if (!Array.isArray(urls)) return [];
  return urls.map((u) => (typeof u === "string" ? u.trim() : "")).filter(Boolean);
}

/**
 * Parses paginated / wrapped public projects list responses (aligned with backoffice list parsing).
 * @param {unknown} json
 * @returns {{ items: unknown[], total: number }}
 */
export function parseProjectsListResponse(json) {
  let root = json;
  if (json && typeof json === "object" && Object.hasOwn(json, "data")) {
    root = json.data;
  }

  if (Array.isArray(root)) {
    return { items: root, total: root.length };
  }

  if (root && typeof root === "object") {
    const items =
      root.items ??
      root.projects ??
      root.results ??
      root.documents ??
      root.records ??
      root.rows ??
      [];

    const arr = Array.isArray(items) ? items : [];
    const meta = root.meta ?? root.pagination ?? {};
    const total =
      root.total ??
      root.totalCount ??
      root.count ??
      meta.total ??
      meta.totalCount ??
      (typeof root.totalPages === "number" && typeof root.limit === "number"
        ? root.totalPages * root.limit
        : arr.length);

    const n = Number(total);
    return { items: arr, total: Number.isFinite(n) ? n : arr.length };
  }

  return { items: [], total: 0 };
}

/** @returns {unknown[]} */
export function normalizeProjectsListResponse(body) {
  return parseProjectsListResponse(body).items;
}

export const PROJECT_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  // { value: "draft", label: "Draft" },
  // { value: "on_hold", label: "On Hold" },
  // { value: "planning", label: "Planning" },
];

/** Public GET `/api/projects` with optional filters (caller supplies keys, e.g. `search`, `industry`, `status`, `sort`). */
export function buildProjectsListUrl(query = {}) {
  query.isSample = true;
  const qs = buildQueryString(query);
  return qs ? `${API_ENDPOINTS.projects}?${qs}` : API_ENDPOINTS.projects;
}

export function extractProjectDetailPayload(resp) {
  const root = resp?.data ?? resp ?? {};
  return root?.project ?? root;
}

/** Financial stats may live on the project root or under `sidebar` (list/detail APIs). */
function getProjectField(project, key) {
  const value = project?.[key];
  if (value != null && value !== "") return value;
  return project?.sidebar?.[key];
}

function getProjectAmount(project, key) {
  const n = Number(getProjectField(project, key));
  return Number.isFinite(n) ? n : 0;
}

function getProjectStringField(project, ...keys) {
  for (const key of keys) {
    const value = getProjectField(project, key);
    const trimmed = String(value ?? "").trim();
    if (trimmed) return trimmed;
  }
  return "";
}

/** Financial stats for card/detail UI (mapped campaign or raw API + `sidebar`). */
export function getCampaignFinancialStats(campaign) {
  if (!campaign || typeof campaign !== "object") {
    return {
      fundsRaised: 0,
      hardCap: 0,
      totalFundsRequired: 0,
      investorCount: 0,
      minimumInvestmentAmount: 0,
      maximumInvestmentAmount: 0,
      sharePriceAmount: 0,
    };
  }

  const pickAmount = (...keys) => {
    for (const key of keys) {
      const fromRoot = campaign[key];
      if (fromRoot != null && fromRoot !== "") {
        const n = Number(fromRoot);
        if (Number.isFinite(n)) return n;
      }
      const fromSidebar = campaign.sidebar?.[key];
      if (fromSidebar != null && fromSidebar !== "") {
        const n = Number(fromSidebar);
        if (Number.isFinite(n)) return n;
      }
    }
    return 0;
  };

  const shareRaw =
    campaign.sharePriceAmount ??
    campaign.sidebar?.sharePrice ??
    campaign.sharePrice;
  const shareN = Number(shareRaw);

  return {
    fundsRaised: pickAmount("fundsRaised", "raised"),
    hardCap: pickAmount("hardCap"),
    totalFundsRequired: pickAmount("totalFundsRequired"),
    investorCount: pickAmount("investorCount", "investors"),
    minimumInvestmentAmount: pickAmount(
      "minimumInvestmentAmount",
      "minInvestment"
    ),
    maximumInvestmentAmount: pickAmount(
      "maximumInvestmentAmount",
      "maxInvestment"
    ),
    sharePriceAmount: Number.isFinite(shareN) ? shareN : 0,
  };
}

/** Shape expected by `CampaignCard` + opportunities filters (`category`, `industryId`). */
export function mapProjectToCampaignCard(project, { descriptionMaxLen = 50 } = {}) {
  if (!project || typeof project !== "object") return null;
  const id = String(project._id ?? project.id ?? "");
  if (!id) return null;

  const raised = getProjectAmount(project, "fundsRaised");
  const hardCap = getProjectAmount(project, "hardCap");
  const totalFunds = getProjectAmount(project, "totalFundsRequired");
  const goal =
    hardCap ||
    totalFunds ||
    getProjectAmount(project, "maximumRaise") ||
    0;

  const rawShare = Number(getProjectField(project, "sharePrice"));
  const sharePriceAmount = Number.isFinite(rawShare) ? rawShare : 0;

  const daysLeft =
    project.campaignDaysLeft == null
      ? null
      : Number(project.campaignDaysLeft);
  const rawStatus = String(project.status || "").toLowerCase();
  const isCampaignExpired =
    daysLeft !== null && !Number.isNaN(daysLeft) && daysLeft <= 0;
  const status =
    rawStatus === "closed" || isCampaignExpired ? "closed" : "active";

  const plainDesc = stripHtml(project.description);
  const industry = project.industry;

  return {
    id,
    industryId: industry?._id == null ? "" : String(industry._id),
    title: project.title || "Untitled",
    description: truncatePlainText(plainDesc, descriptionMaxLen),
    image: getProjectCoverImage(project) || "",
    raised,
    goal,
    fundsRaised: raised,
    hardCap,
    totalFundsRequired: totalFunds,
    investorCount: getProjectAmount(project, "investorCount"),
    minimumInvestmentAmount: getProjectAmount(project, "minimumInvestmentAmount"),
    maximumInvestmentAmount: getProjectAmount(project, "maximumInvestmentAmount"),
    sharePriceAmount,
    category: industry?.name || industry?.slug || "—",
    investorsNeeded: 0,
    investors: getProjectAmount(project, "investorCount"),
    daysRemaining: daysLeft != null && !Number.isNaN(daysLeft) ? daysLeft : 0,
    campaignStartAt: project.campaignStartAt || null,
    campaignEndAt: project.campaignEndAt || null,
    status,
    isSample: Boolean(project.isSample),
    createdAt: project.createdAt || project.updatedAt || null,
  };
}

/** Maps API `canInvest` to true/false, or `undefined` when the field is absent. */
export function parseProjectCanInvest(value) {
  if (value === true) return true;
  if (value === false) return false;
  return undefined;
}

export function mapProjectToDetailView(project) {
  if (!project || typeof project !== "object") return null;
  const id = String(project._id ?? project.id ?? "");
  const rawSharePrice = Number(getProjectField(project, "sharePrice"));
  const sharePrice = rawSharePrice > 0 ? rawSharePrice : 1;
  const raised = getProjectAmount(project, "fundsRaised");
  const hardCap = getProjectAmount(project, "hardCap");
  const totalFunds = getProjectAmount(project, "totalFundsRequired");
  let goal = 1;
  if (hardCap > 0) goal = hardCap;
  else if (totalFunds > 0) goal = totalFunds;
  const daysLeft =
    project.campaignDaysLeft != null && project.campaignDaysLeft !== ""
      ? Number(project.campaignDaysLeft)
      : null;
  const rawStatus = String(project.status || "").trim().toLowerCase();

  const sharesOffered =
    sharePrice > 0 && goal > 0 ? Math.floor(goal / sharePrice) : 0;

  const team = Array.isArray(project.teamMembers)
    ? project.teamMembers.map((m) => ({
        name: m.name || "",
        role: m.position || "",
        image: m.imageUrl || "",
        municipality: m.municipalityOfResidence || "",
        email: m.email || "",
        principalOccupation: m.principalOccupation || "",
        aboutHtml: m.about || "",
      }))
    : [];

  const documents = Array.isArray(project.offeringDocuments)
    ? project.offeringDocuments.map((d) => {
        const url = d.url || "";
        const ext = url.split(".").pop()?.toLowerCase();
        let type = "File";
        if (ext === "pdf") type = "PDF";
        else if (ext) type = ext.toUpperCase();
        return { name: d.name || "Document", type, url };
      })
    : [];

  const issuerName = getProjectStringField(project, "issuerName", "issuer_name");
  const typeOfEligibleSecurityOffered = getProjectStringField(
    project,
    "typeOfEligibleSecurityOffered",
    "type_of_eligible_security_offered",
    "typeOfEligibleSecurity"
  );

  return {
    id,
    title: project.title || "Untitled",
    issuerName,
    typeOfEligibleSecurityOffered,
    description: truncatePlainText(stripHtml(project.description), 160),
    longDescriptionHtml: project.description || "",
    companyOverviewHtml: project.companyOverview || "",
    image: getProjectCoverImage(project) || "",
    galleryImages: getProjectGalleryImages(project),
    category: project.industry?.name || project.industry?.slug || "—",
    raised,
    goal,
    minInvestment: getProjectAmount(project, "minimumInvestmentAmount"),
    maxInvestment: getProjectAmount(project, "maximumInvestmentAmount"),
    investors: getProjectAmount(project, "investorCount"),
    daysRemaining: daysLeft,
    campaignStartAt: project.campaignStartAt || null,
    campaignEndAt: project.campaignEndAt || null,
    status: rawStatus,
    canInvest: parseProjectCanInvest(project.canInvest),
    valuation: null,
    sharePrice,
    /** Raw API share price for display (may be 0). */
    sharePriceAmount: Number.isFinite(rawSharePrice) ? rawSharePrice : 0,
    sharesOffered,
    fundsRaised: raised,
    hardCap,
    totalFundsRequired: totalFunds,
    investorCount: getProjectAmount(project, "investorCount"),
    minimumInvestmentAmount: getProjectAmount(project, "minimumInvestmentAmount"),
    maximumInvestmentAmount: getProjectAmount(project, "maximumInvestmentAmount"),
    company: {
      name: issuerName || project.title || "—",
      founded: "—",
      employees: "—",
      location: project.companyHeadOfficeAddress || "—",
      website: project.companyWebsite || "",
      industry: project.industry?.name || "—",
    },
    companyEmail: project.companyEmail || "",
    companyPhone: project.companyPhone || "",
    team,
    documents,
    newsUpdates: Array.isArray(project.newsUpdates) ? project.newsUpdates : [],
    isSample: Boolean(project.isSample),
  };
}